import React, { useEffect } from 'react';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { useEmailValidation } from '@/hooks/useEmailValidation';
import { Tooltip } from '@/Components/ui/Tooltip';

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  checkEndpoint: string;
  excludeId?: string;
  externalError?: string;
  dataTestId?: string;
  className?: string;
  size?: 'default' | 'sm';
  tooltip?: string;
  /** Attribut HTML `autocomplete` : "off" quand l'e-mail n'est pas celui du compte connecté (création d'un compte). */
  autoComplete?: string;
  onValidationChange?: (state: { error: string | null; isChecking: boolean }) => void;
}

export const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  label = 'Email',
  placeholder = 'exemple@domain.com',
  required = false,
  disabled = false,
  checkEndpoint,
  excludeId,
  externalError,
  dataTestId,
  className = '',
  size = 'default',
  tooltip,
  autoComplete,
  onValidationChange,
}) => {
  const { error, isChecking, validateEmail } = useEmailValidation({
    endpoint: checkEndpoint,
    debounceMs: 500,
    excludeId,
  });

  useEffect(() => {
    onValidationChange?.({ error, isChecking });
  }, [error, isChecking]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toLowerCase();
    onChange(newValue);
    validateEmail(newValue);
  };

  const displayError = externalError || error;
  const hasError = !!displayError;
  const isValid = !hasError && !isChecking && value.trim().length > 0;
  const hasValue = value.trim().length > 0;

  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (sm ? 'tw-py-2' : 'tw-py-2.5');
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm
    ? 'tw-top-[9px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1 peer-focus:tw-text-[10px]'
    : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px]';

  return (
    <div className={className}>
      <div className="tw-relative">
        <Mail className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400 tw-z-10" />

        <input
          type="email"
          data-testid={dataTestId}
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          placeholder={label || placeholder || ' '}
          className={`tw-w-full tw-pl-10 tw-pr-10 ${paddingClass} ${textClass} tw-border tw-rounded-xl tw-transition-all tw-peer tw-placeholder-transparent
            ${hasError ? 'tw-border-red-500' : isValid ? 'tw-border-green-400' : 'tw-border-gray-200'}
            ${disabled ? 'tw-bg-gray-50 tw-text-gray-500 tw-cursor-not-allowed' : ''}
            focus:tw-outline-none focus:tw-ring-2
            ${hasError ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary'}`}
        />

        {label && (
          <label
            className={`tw-absolute tw-left-10 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${hasValue
                ? labelFloated
                : labelUnfloated
              }
              ${hasError
                ? (hasValue ? 'tw-text-red-500' : 'peer-focus:tw-text-red-500')
                : (hasValue ? 'tw-text-affinicar-primary' : 'peer-focus:tw-text-affinicar-primary')
              }
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}

        <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
          {isChecking && (
            <Loader2 className="tw-w-4 tw-h-4 tw-text-affinicar-primary tw-animate-spin" />
          )}
          {isValid && (
            <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-green-500" />
          )}
        </div>
      </div>

      {displayError && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{displayError}</p>
      )}
    </div>
  );
};
